// Format a start-end pair for display.
// (null, 0.64)  → "≤0.64"
// (0.64, null)  → ">0.64"
// (0, 84.8)     → "0-84.8"
// (null, null)  → null  (caller omits this part)
export const formatRange = (start, end) => {
  if (start == null && end == null) return null
  if (start == null) return `≤${end}`
  if (end == null) return `>${start}`
  return `${start}-${end}`
}

// Build the legend label for a single curve.
// Order: {calibration} {model} {...dimension labels in cascade order}
// _dimLabels is pre-computed by the page when building the visible curves (one
// entry per active dimension, e.g. expression + direction for wind). Average
// curves carry none, so they render as just "{calibration} {model}".
export const buildCurveLabel = (modelName, curve) => {
  const parts = []
  if (curve.calibration && !curve._hideCalibration)
    parts.push(curve.calibration)
  parts.push(modelName)
  if (Array.isArray(curve._dimLabels)) parts.push(...curve._dimLabels)
  return parts.join(' ')
}

// One base hex color per model name.
export const MODEL_BASE_COLORS = {
  AIFS: 0x1f77b4,
  AROME_FR_HD: 0xff7f0e,
  ARPEGE_EU: 0x2ca02c,
  DMI: 0xd62728,
  ECMWF: 0x368ed8,
  ETAEC: 0x8c564b,
  GEM: 0x006400,
  GFS_ETA_015: 0xe377c2,
  ICON: 0x17becf,
  ICON_D2: 0x8b0000,
  ICON_EU: 0xbcbd22,
  KNMI_EU: 0x9467bd,
  KNMI_NL: 0x4b0082,
  UKMO: 0xff9896,
  UKMO100: 0xaec7e8,
  UKMO_2K: 0xffa500,
}

// ── Normalization ─────────────────────────────────────────────────────────
// Turn the raw API/mock payload into the shape the page consumes.
//
//   { plant, type, calibrations[], averageCurves[], models[] }
//
// Each model: { name, calibrations[], averageCurves[], curves[] }
//  - averageCurves: curves carrying only { average:true, calibration, data }
//  - curves:        dimensional curves (average ones removed)
//
// Selector options are derived per calibration at render time (see the model
// tab's cascade), so normalization only splits curves and lists calibrations.

// Compare two range bounds; null sorts first. Works for numbers and strings.
const compareBound = (a, b) => {
  const av = a == null ? -Infinity : a
  const bv = b == null ? -Infinity : b
  if (av < bv) return -1
  if (av > bv) return 1
  return 0
}

// Collect the distinct {start, end} ranges for one dimension across curves.
// A curve may carry only one side (e.g. expression_end → "≤X", or
// expression_start → ">X"); missing sides become null.
export const extractRanges = (curves, prefix) => {
  const startField = `${prefix}_start`
  const endField = `${prefix}_end`
  const seen = new Set()
  const out = []
  curves.forEach(c => {
    const hasStart = startField in c
    const hasEnd = endField in c
    if (!hasStart && !hasEnd) return
    const start = hasStart ? c[startField] : null
    const end = hasEnd ? c[endField] : null
    if (start == null && end == null) return
    const key = `${start}__${end}`
    if (seen.has(key)) return
    seen.add(key)
    out.push({
      value: JSON.stringify({ start, end }),
      label: formatRange(start, end),
      start,
      end,
    })
  })
  out.sort(
    (a, b) => compareBound(a.start, b.start) || compareBound(a.end, b.end),
  )
  return out
}

const sortCalibrationsDesc = values =>
  [...new Set(values)].filter(Boolean).sort().reverse()

// Normalize the (variable-length) model weights to a simple { name, weight }[].
// Read defensively so it works across a few shapes:
//   { "ETAEC": 0.084, "ECMWF": 0.073, ... }    ← flat map of name → weight (current)
//   { "ModelId": "ECMWF", "Weight": 0.486 }    ← name as an explicit field's value
//   { "name": "ECMWF", "Weight": 0.486 }
//   { "ECMWF": 2, "Weight": 0.486 }             ← name as the (dynamic) property key
const WEIGHT_KEYS = ['Weight', 'weight']
const NAME_KEYS = ['ModelId', 'name', 'Name', 'Model']
const normalizeWeights = raw => {
  const rawWeights = raw?.weights
  if (rawWeights == null) return []

  // Current shape: a flat object mapping model name → weight.
  if (!Array.isArray(rawWeights)) {
    return Object.entries(rawWeights)
      .map(([name, weight]) => ({ name, weight }))
      .filter(w => w.name != null && w.weight != null)
  }

  // Legacy shape: an array of per-model objects (a few possible layouts).
  return rawWeights
    .map(w => {
      const weight = w.Weight ?? w.weight
      // 1) explicit, string-valued name field
      let name = NAME_KEYS.map(k => w[k]).find(v => typeof v === 'string')
      // 2) fallback: the model name is the property key that isn't weight/id
      if (name == null) {
        name = Object.keys(w).find(
          k => !WEIGHT_KEYS.includes(k) && !NAME_KEYS.includes(k),
        )
      }
      return { name, weight }
    })
    .filter(w => w.name != null && w.weight != null)
}

export const normalizePowerCurveData = raw => {
  const type = raw?.type
  const models = (raw?.models ?? []).map(model => {
    const averageCurves = []
    const curves = []
    ;(model.curves ?? []).forEach(c => {
      const tagged = { ...c, _modelName: model.name }
      if (c.average === true) averageCurves.push(tagged)
      else curves.push(tagged)
    })

    return {
      name: model.name,
      calibrations: sortCalibrationsDesc(
        (model.curves ?? []).map(c => c.calibration),
      ),
      averageCurves,
      curves,
    }
  })

  return {
    plant: raw?.plant,
    type,
    calibrations: sortCalibrationsDesc(models.flatMap(m => m.calibrations)),
    averageCurves: models.flatMap(m => m.averageCurves),
    weights: normalizeWeights(raw),
    models,
  }
}

const hexToRgb = hex => ({
  r: (hex >> 16) & 0xff,
  g: (hex >> 8) & 0xff,
  b: hex & 0xff,
})

const rgbToHex = (r, g, b) => (r << 16) | (g << 8) | b

// Generate n shades from base color (darkest) toward white (lightest).
// Max lightening is 55% toward white so shades stay recognisably the same hue.
export const generateModelShades = (baseHex, n) => {
  if (n <= 0) return []
  if (n === 1) return [baseHex]
  const { r, g, b } = hexToRgb(baseHex)
  return Array.from({ length: n }, (_, i) => {
    const t = (i / (n - 1)) * 0.55
    return rgbToHex(
      Math.round(r + (255 - r) * t),
      Math.round(g + (255 - g) * t),
      Math.round(b + (255 - b) * t),
    )
  })
}
